import type { KnownSizeUploadObject } from "./uploadObjectManifest";
import { hasPersistedUploadFacadeErrorCode, readUploadFacadeErrorCode } from "./uploadFacadeErrors";

export const UPLOAD_CONTROL_UNREADABLE = "UPLOAD_CONTROL_UNREADABLE";
/** Each host supplies its configured support address at presentation time. */
export function uploadControlHeldMessage(supportAddress: string): string {
	return `Your upload is paused, and we're keeping your saved data. Please email ${supportAddress} for help.`;
}

/** Control uncertainty cannot prove source loss or authorize a replacement upload. */
export function isUploadControlUnavailable(error: unknown): boolean {
	return (
		readUploadFacadeErrorCode(error) === UPLOAD_CONTROL_UNREADABLE ||
		hasPersistedUploadFacadeErrorCode(error, UPLOAD_CONTROL_UNREADABLE)
	);
}

export function uploadControlError(message: string, cause?: unknown): Error & { code: string } {
	return Object.assign(new Error(`${message} [${UPLOAD_CONTROL_UNREADABLE}]`, { cause }), { code: UPLOAD_CONTROL_UNREADABLE });
}

export interface UploadKnownSizeControl {
	contractVersion: 2;
	finalizationId: string;
	object: KnownSizeUploadObject;
	objectId?: string;
}
export interface UploadObjectControlRow {
	contractVersion?: 1 | 2;
	objectUploadJson?: string;
	protocolSelectionPending?: boolean;
}
const uuid = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/.test(value);
const hash = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
function invalid(): never {
	throw uploadControlError("Saved upload object control is unreadable or differs from its source");
}

/** A v2 marker without its immutable control is blocked recovery, never a legacy source. */
export function parseUploadKnownSizeControl(value: unknown, requireMapping = false): UploadKnownSizeControl {
	if (!value || typeof value !== "object") invalid();
	const control = value as UploadKnownSizeControl;
	const object = control.object;
	if (
		control.contractVersion !== 2 ||
		!uuid(control.finalizationId) ||
		((requireMapping || control.objectId !== undefined) && !uuid(control.objectId)) ||
		!object ||
		!uuid(object.clientObjectId) ||
		object.sequence !== 0 ||
		object.runId !== undefined ||
		object.fragmentIndex !== undefined ||
		object.fragmentOffset !== undefined ||
		object.fragmentByteSize !== undefined ||
		!hash(object.manifestSha256) ||
		!Number.isSafeInteger(object.byteSize) ||
		object.byteSize < 1 ||
		object.byteSize > 5 * 1024 ** 4 ||
		typeof object.contentType !== "string" ||
		!object.contentType ||
		object.contentType.length > 255 ||
		/[\r\n]/.test(object.contentType) ||
		!Array.isArray(object.parts) ||
		object.parts.length !== object.partCount ||
		!Number.isInteger(object.partCount) ||
		object.partCount < 1 ||
		object.partCount > 10000
	)
		invalid();
	let offset = 0;
	for (const [index, part] of object.parts.entries()) {
		if (
			!part ||
			part.ordinal !== index + 1 ||
			part.offset !== offset ||
			!Number.isSafeInteger(part.byteSize) ||
			part.byteSize < 1 ||
			part.byteSize > 5 * 1024 ** 3 ||
			(index < object.parts.length - 1 && part.byteSize < 5 * 1024 ** 2) ||
			!hash(part.sha256)
		)
			invalid();
		offset += part.byteSize;
	}
	if (offset !== object.byteSize) invalid();
	return control;
}
export function readUploadKnownSizeControl(row: UploadObjectControlRow, requireMapping = false): UploadKnownSizeControl | undefined {
	if (row.protocolSelectionPending && (row.contractVersion !== undefined || row.objectUploadJson !== undefined)) invalid();
	if ((row.contractVersion === undefined || row.contractVersion === 1) && row.objectUploadJson === undefined) return undefined;
	if (row.contractVersion !== 2 || typeof row.objectUploadJson !== "string") invalid();
	let control: unknown;
	try {
		control = JSON.parse(row.objectUploadJson);
	} catch {
		invalid();
	}
	return parseUploadKnownSizeControl(control, requireMapping);
}

/** Every target writer must retain the source's protocol pin and exact object mapping. */
export function uploadObjectControlColumns(
	target: { contractVersion?: 2; objectUpload?: UploadKnownSizeControl } | undefined,
	existing?: UploadObjectControlRow
): UploadObjectControlRow {
	if (target?.contractVersion === 2) {
		const bound = parseUploadKnownSizeControl(target.objectUpload, true);
		const source = existing ? readUploadKnownSizeControl(existing) : undefined;
		if (
			source &&
			(source.finalizationId !== bound.finalizationId ||
				source.object.clientObjectId !== bound.object.clientObjectId ||
				source.object.manifestSha256 !== bound.object.manifestSha256 ||
				source.object.byteSize !== bound.object.byteSize ||
				source.object.contentType !== bound.object.contentType ||
				(source.objectId !== undefined && source.objectId !== bound.objectId) ||
				source.object.parts.some((part, index) => {
					const next = bound.object.parts[index];
					return (
						!next ||
						part.ordinal !== next.ordinal ||
						part.offset !== next.offset ||
						part.byteSize !== next.byteSize ||
						part.sha256 !== next.sha256
					);
				}))
		)
			invalid();
		if (existing?.contractVersion === 1) invalid();
		return { contractVersion: 2, objectUploadJson: JSON.stringify(bound), protocolSelectionPending: false };
	}
	if (existing?.contractVersion === 2 || existing?.objectUploadJson !== undefined) invalid();
	return existing?.contractVersion === 1 || existing?.protocolSelectionPending
		? { contractVersion: 1, protocolSelectionPending: false }
		: {};
}
